import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Typography,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { themeBlue, themeOrange } from "../../../utils/colorTheme";
import { MdCancel } from "react-icons/md";

const SubscriptionsTable = ({ subscriptions, onCancelSubscription, cancellingId }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusChip = (status) => {
    if (status === "active") {
      return (
        <Chip
          label="Active"
          size="small"
          sx={{
            backgroundColor: "#dcfce7",
            color: "#22c55e",
            fontWeight: 600,
          }}
        />
      );
    }
    return (
      <Chip
        label="Expired"
        size="small"
        sx={{
          backgroundColor: "#fee2e2",
          color: "#ef4444",
          fontWeight: 600,
        }}
      />
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: "0px 4px 10px 0px #00000026",
        borderRadius: "12px",
        overflowX: "auto", // horizontal scroll so Cancel stays reachable on narrow screens / mobile
      }}
    >
      <Table sx={{ minWidth: 780 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: themeBlue }}>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Subscriber</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Plan</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Start Date</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Expiry Date</TableCell>
            <TableCell sx={{ color: "white", fontWeight: 600 }}>Status</TableCell>
            <TableCell
              sx={{
                color: "white",
                fontWeight: 600,
                position: "sticky",
                right: 0,
                backgroundColor: themeBlue,
                width: 60,
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptions.map((subscription, index) => (
            <TableRow
              key={subscription?.id || index}
              sx={{
                "&:hover": { backgroundColor: "#f5f5f5" },
                "&:last-child td, &:last-child th": { border: 0 },
              }}
            >
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Avatar
                    src={subscription?.user?.avatar}
                    sx={{ width: 40, height: 40 }}
                  >
                    {subscription?.user?.first_name?.[0]}
                  </Avatar>
                  <Typography sx={{ fontWeight: 500 }}>
                    {subscription?.user?.first_name} {subscription?.user?.last_name}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: "#666" }}>
                  {subscription?.user?.email || "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={subscription?.plan_name || "Membership"}
                  size="small"
                  sx={{
                    backgroundColor: "#fff4e6",
                    color: themeOrange,
                    fontWeight: 600,
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography sx={{ color: "#666" }}>
                  {formatDate(subscription?.start_date)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: "#666" }}>
                  {formatDate(subscription?.expiry_date)}
                </Typography>
              </TableCell>
              <TableCell>{getStatusChip(subscription?.status)}</TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  right: 0,
                  backgroundColor: "white",
                  boxShadow: "-2px 0 4px rgba(0,0,0,0.04)",
                  width: 60,
                  padding: "8px",
                }}
              >
                {subscription?.status === "active" && (
                  <Tooltip title="Cancel subscription" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onCancelSubscription(subscription?.user?.id)}
                        disabled={cancellingId === subscription?.user?.id}
                        sx={{
                          backgroundColor: "#ef4444",
                          color: "white",
                          width: 32,
                          height: 32,
                          "&:hover": { backgroundColor: "#dc2626" },
                          "&:disabled": { backgroundColor: "#fca5a5", color: "white" },
                        }}
                      >
                        {cancellingId === subscription?.user?.id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <MdCancel size={18} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SubscriptionsTable;
